export const LOBBY_STATUS = {
	LOBBY: 'lobby',
	PLAYING: 'playing',
};

export const PLATFORM = {
	POKI: 1,
	MOBILE: 2,
};

export const MIN_LOBBY_PLAYERS = 2;
export const MAX_PASSWORD_LENGTH = 32;
export const MAX_ROOM_NAME_LENGTH = 24;
const MAX_HOST_NAME_LENGTH = 32;
const MAX_LEVEL_TITLE_LENGTH = 64;

export const clampMaxPlayers = (value, limit) => {
	const players = Math.round(Number(value));
	return Number.isFinite(players) ? Math.max(MIN_LOBBY_PLAYERS, Math.min(limit, players)) : limit;
};

export const normalizePassword = value => typeof value === 'string' ? value.trim().slice(0, MAX_PASSWORD_LENGTH) : '';

// Short codes use an uppercase alphabet; longer codes are case sensitive.
export const normalizeLobbyCode = value => {
	if (typeof value !== 'string') return '';
	const code = value.replace(/\s+/g, '').slice(0, 20);
	return code.length <= 5 ? code.toUpperCase() : code;
};

const ROOM_ADJECTIVES = ['Jolly', 'Wobbly', 'Turbo', 'Crispy', 'Sneaky', 'Rusty', 'Bouncy', 'Spicy', 'Clumsy', 'Mighty',
	'Soggy', 'Speedy', 'Grumpy', 'Funky', 'Wild', 'Lucky', 'Cheeky', 'Shiny', 'Fearless', 'Dizzy'];
const ROOM_NOUNS = ['Bananas', 'Ragdolls', 'Skeletons', 'Pirates', 'Pancakes', 'Penguins', 'Wizards', 'Llamas', 'Noodles', 'Daredevils',
	'Stuntmen', 'Unicycles', 'Cannonballs', 'Meatballs', 'Jellybeans', 'Ninjas', 'Tacos', 'Crash Dummies', 'Monkeys', 'Hamsters'];

export const normalizeRoomName = value => typeof value === 'string'
	? value.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, MAX_ROOM_NAME_LENGTH).trim() : '';

export const randomRoomName = (random = Math.random) =>
	`${ROOM_ADJECTIVES[Math.floor(random() * ROOM_ADJECTIVES.length)]} ${ROOM_NOUNS[Math.floor(random() * ROOM_NOUNS.length)]}`;

const generatedRoomName = seed => {
	const hash = [...String(seed)].reduce((value, char) => (value * 31 + char.charCodeAt(0)) >>> 0, 7);
	return `${ROOM_ADJECTIVES[hash % ROOM_ADJECTIVES.length]} ${ROOM_NOUNS[Math.floor(hash / ROOM_ADJECTIVES.length) % ROOM_NOUNS.length]}`;
};

export const isGeneratedRoomName = name => ROOM_ADJECTIVES.some(adjective => name.startsWith(`${adjective} `) && ROOM_NOUNS.includes(name.slice(adjective.length + 1)));

// Poki players only see generated names, never text another player typed.
export const lobbyRoomName = (customData, code, generatedOnly = false) => {
	const name = normalizeRoomName(customData?.name);
	return name && (!generatedOnly || isGeneratedRoomName(name)) ? name : generatedRoomName(code);
};

// The backend refuses levels an admin hid on Poki or on mobile to those players.
export const levelHiddenOn = level => (level?.hidden_on_poki ? PLATFORM.POKI : 0) | (level?.hidden_on_mobile ? PLATFORM.MOBILE : 0);

// Fixed-width digits sort like versions as text. Netlib casts lobby data to numeric for number
// filters, so one lobby with a non-numeric value would break the list for everyone.
export const versionKey = version => String(version).split('.').concat('0', '0').slice(0, 3)
	.map(part => String(Math.min(999, parseInt(part, 10) || 0)).padStart(3, '0')).join('');
export const VERSION_KEY_PATTERN = '^[0-9]{9}$';
export const isVersionKey = value => typeof value === 'string' && new RegExp(VERSION_KEY_PATTERN).test(value);

export const lobbyCustomData = ({version, host, name, level, status}) => ({
	v: versionKey(version),
	name: normalizeRoomName(name),
	host: String(host || '').slice(0, MAX_HOST_NAME_LENGTH),
	level: level ? {id: String(level.id), title: String(level.title || '').slice(0, MAX_LEVEL_TITLE_LENGTH), hidden: levelHiddenOn(level)} : null,
	status: status === LOBBY_STATUS.PLAYING ? LOBBY_STATUS.PLAYING : LOBBY_STATUS.LOBBY,
});

export const isCompatibleLobby = (lobby, version) => lobby?.customData?.v === versionKey(version);
export const isFullLobby = lobby => lobby.maxPlayers > 0 && lobby.playerCount >= lobby.maxPlayers;

const latencyOf = lobby => Number.isFinite(lobby.latency) ? lobby.latency : 1000;
const latencyBand = lobby => latencyOf(lobby) < 150 ? 0 : latencyOf(lobby) < 300 ? 1 : 2;
const playing = lobby => lobby.customData?.status === LOBBY_STATUS.PLAYING ? 1 : 0;

const levelBlocked = (lobby, client) => (lobby.customData?.level?.hidden & client.platform) > 0;

// client: {version, platform}. A running lobby would load its level straight away,
// so hide the ones this player cannot load.
export const browsableLobbies = (lobbies, client) => lobbies
	.filter(lobby => isCompatibleLobby(lobby, client.version) && lobby.playerCount > 0 && !(playing(lobby) && levelBlocked(lobby, client)))
	.sort((a, b) => isFullLobby(a) - isFullLobby(b) || playing(a) - playing(b) || a.hasPassword - b.hasPassword
		|| latencyBand(a) - latencyBand(b) || b.playerCount - a.playerCount || latencyOf(a) - latencyOf(b));

export const quickPlayLobbies = (lobbies, client) => browsableLobbies(lobbies, client)
	.filter(lobby => !lobby.hasPassword && !isFullLobby(lobby) && !levelBlocked(lobby, client));

export const JOIN_ERROR = {
	NOT_FOUND: 'lobby-not-found',
	WRONG_PASSWORD: 'invalid-password',
	FULL: 'lobby-is-full',
	UPDATE_REQUIRED: 'update-required',
	LOBBY_OUTDATED: 'lobby-outdated',
	CONNECTION: 'connection',
};

export const joinErrorCode = error => Object.values(JOIN_ERROR).includes(error?.code) ? error.code : JOIN_ERROR.CONNECTION;
